/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ElementType, useMemo, useEffect } from 'react';
import clsx from 'clsx';

// components
import Account from '@/components/modules/Account';
import ListItem from '@/components/elements/ListItem';

import { useMediaQuery } from 'react-responsive';

// icons
import {
  Portfolios as PortfoliosIcon,
  Exchange as ExchangeIcon,
  Subscription as SubscriptionIcon,
  Dashboard as DashboardIcon,
  AngleRightDouble,
  Settings,
} from '@/components/modules/icons';
import NavLink from '@/components/elements/NavLink';
import Button from '@/components/elements/Button';
// icons
import AngleLeftDouble from '@/components/modules/icons/AngleLeftDouble';

// store
import { useSidebar } from '@/store';

// utils
import { groupBy } from '@/utils';
import { useAuth } from '@/providers/AuthProvider';
import Typography from '@/components/elements/Typography';
import { isMobile } from 'react-device-detect';
import LogoWithTitle from '../LogoWithTitle';
import UserAvatar from '../UserAvatar';

export type MenuGroup = {
  label: string;
  type?: 'ListItem' | 'Link';
};

export type SidebarMenuItem = {
  label: string;
  to?: string;
  icon?: ElementType;
  disabled?: boolean;
  group: MenuGroup;
  hide?: boolean;
};

const MainGroup: MenuGroup = {
  label: `Main Menu`,
  type: `ListItem`,
};

const OtherGroup: MenuGroup = {
  label: `Other`,
  type: `Link`,
};

export type SidebarProps = {
  sidebarMenu?: SidebarMenuItem[];
};

const desktopSidebarMenu: SidebarMenuItem[] = [
  {
    label: `Dashboard`,
    icon: DashboardIcon,
    to: `/`,
    group: MainGroup,
  },
  {
    label: `My exchanges`,
    icon: ExchangeIcon,
    to: `/exchanges`,
    group: MainGroup,
  },
  {
    label: `Subscription`,
    icon: SubscriptionIcon,
    to: `/subscription`,
    group: MainGroup,
    hide: isMobile,
  },
  {
    label: `Portfolios`,
    to: `/portfolio`,
    icon: PortfoliosIcon,
    group: MainGroup,
  },

  {
    label: `FAQ`,
    to: `/faq`,
    group: OtherGroup,
  },
  {
    label: `Blog`,
    to: `/blog`,
    group: OtherGroup,
  },
  {
    label: `Taking tour`,
    to: `/tour`,
    group: OtherGroup,
  },
  {
    label: `Settings`,
    to: `/settings`,
    icon: Settings,
    group: MainGroup,
  },
];

/**
 * @component Sidebar
 */
// eslint-disable-next-line no-empty-pattern
function Sidebar({ sidebarMenu = desktopSidebarMenu }: SidebarProps) {
  const { account } = useAuth();
  const { collapse, setCollapse } = useSidebar();

  const isTabletOrMobile = useMediaQuery({ maxWidth: 1024 });
  const isMobileBreakpoint = useMediaQuery({ maxWidth: 768 });

  const isMobileBreakpointAndClosed = collapse && isMobileBreakpoint;
  const isMobileBreakpointAndOpen = !collapse && isMobileBreakpoint;
  const isDesktopAndOpen = !isMobileBreakpoint && !collapse;
  const isDesktopnAndCollapsed = !isMobileBreakpoint && collapse;

  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  useEffect(() => {
    if (!isTabletOrMobile && collapse) return;
    setCollapse(isTabletOrMobile);
  }, [isTabletOrMobile]);

  // Hide body scroll on drawer open
  useEffect(() => {
    if (isMobileBreakpoint && !collapse) {
      document.body.style.overflow = `hidden`;
    } else {
      document.body.style.overflow = `auto`;
    }
  }, [collapse, isMobileBreakpoint]);

  const classes = clsx(
    `fixed flex flex-col z-[99] top-0 left-0 transition-all pb-16 md:pb-0 duration-300 overflow-y-auto overflow-x-hidden whitespace-nowrap shadow-sm h-screen px-3 bg-white`,
    {
      'w-[85px]': isDesktopnAndCollapsed,
      'w-[240px] translate-x-0': isMobileBreakpointAndOpen || isDesktopAndOpen,
      'transform -translate-x-full': isMobileBreakpointAndClosed,
    },
  );

  const list = useMemo(
    () => groupBy(sidebarMenu, (item) => item.group.label),
    [],
  );

  const renderListItem = ({ label, icon, to, hide }: SidebarMenuItem) => {
    if (hide) return null;

    return (
      <NavLink href={to as string} key={label}>
        {/* eslint-disable-next-line no-shadow */}
        {({ active }) => (
          <ListItem
            data-testid="sidebar_list-item"
            classes={{
              root: clsx({
                '!px-0': isDesktopnAndCollapsed,
                '!px-3': isDesktopAndOpen,
              }),
              text: `ml-3.5`,
              iconContainer: clsx(`transition-all duration-300`, {
                'min-w-[28px]': isDesktopAndOpen,
                'min-w-[61px]': isDesktopnAndCollapsed,
              }),
            }}
            fullWidth
            selected={active}
            button
            icon={icon}
          >
            {label}
          </ListItem>
        )}
      </NavLink>
    );
  };

  const renderLink = ({ label, to }: SidebarMenuItem) => (
    <NavLink className="ml-3.5" href={to as string} key={label}>
      <div
        data-testid="sidebar_link-item"
        className="cursor-pointer hover:text-gray-900 text-gray-500 transition-colors duration-300"
      >
        <Typography variant="metadata">{label}</Typography>
      </div>
    </NavLink>
  );

  return (
    <>
      {isMobileBreakpoint && !collapse && (
        <div
          data-testid="sidebar_backdrop"
          onClick={handleCollapse}
          role="dialog"
          className="fixed bg-white opacity-80 z-[98] inset-0"
        />
      )}
      <div className={classes} data-testid="sidebar">
        <div
          className={clsx(
            `flex items-center justify-center min-h-[112px] h-[112px] lg:h-[140px]`,
          )}
        >
          <div
            className={clsx({
              block: isDesktopAndOpen,
              hidden: isDesktopnAndCollapsed,
            })}
          >
            <Account
              classes={{
                avatarContainer: `m-auto w-[60px] collapse:w-auto`,
              }}
            >
              M
            </Account>
          </div>

          <div
            className={clsx({
              block: isDesktopnAndCollapsed,
              hidden: isDesktopAndOpen || isMobileBreakpoint,
            })}
          >
            <UserAvatar walletAddress={account as string} />
          </div>
        </div>

        <ul className="space-y-12 flex-1">
          {Object.keys(list).map((group) => {
            const groupList = list[group];

            const rootClasses = clsx(`flex flex-col`, {
              'transition-opacity duration-300 opacity-0':
                groupList[0].group.label === `Other` && isDesktopnAndCollapsed,
              'opacity-100':
                groupList[0].group.label === `Other` && isDesktopAndOpen,
              'space-y-6': isDesktopAndOpen,
              'space-y-4': isMobileBreakpoint,
              'space-y-0': isDesktopnAndCollapsed,
            });
            return (
              <div
                data-testid="sidebar_group"
                className={rootClasses}
                key={group}
              >
                <Typography
                  data-testid="sidebar_group_title"
                  className={clsx(`ml-3.5`, {
                    block: isDesktopAndOpen,
                    hidden: isDesktopnAndCollapsed,
                  })}
                  variant="h6"
                >
                  {groupList[0].group.label}
                </Typography>
                <div
                  data-testid="sidebar_group_list"
                  className="flex flex-col space-y-2"
                >
                  {groupList.map((item) => {
                    switch (item.group.type) {
                      case `ListItem`:
                        return renderListItem(item);
                      case `Link`:
                        return renderLink(item);
                      default:
                        return renderListItem(item);
                    }
                  })}
                </div>
              </div>
            );
          })}
        </ul>

        {!isMobileBreakpoint && (
          <div
            className={clsx(`flex flex-col items-center space-y-2`, {
              'py-4 px-0': isDesktopnAndCollapsed,
              'py-6 px-8': isDesktopAndOpen,
            })}
          >
            <div
              className={clsx(`w-full flex justify-center items-center`, {
                'space-x-3 flex-row': isDesktopAndOpen,
                'space-x-0 flex-col-reverse': isDesktopnAndCollapsed,
              })}
            >
              <LogoWithTitle
                classes={{
                  title: clsx({
                    hidden: isDesktopnAndCollapsed,
                    block: isDesktopAndOpen,
                  }),
                }}
              />

              <Button
                data-testid="sidebar_collapse-button"
                onClick={handleCollapse}
                className={clsx(`flex-shrink-0 w-8 h-8 min-w-0 !p-0`, {
                  'mb-0': isDesktopAndOpen,
                  'mb-3': isDesktopnAndCollapsed,
                })}
                size="small"
                variant="outlined"
              >
                {isDesktopAndOpen && <AngleLeftDouble />}
                {isDesktopnAndCollapsed && <AngleRightDouble />}
              </Button>
            </div>
            <Typography
              variant="bodyLight"
              className={clsx(`text-gray-400`, {
                hidden: isDesktopnAndCollapsed,
                block: isDesktopAndOpen,
              })}
            >
              © 2021 All rights reserved.
            </Typography>
          </div>
        )}
      </div>
    </>
  );
}

export default Sidebar;
